import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';

import { ClientCvTemplateService } from '../../templates/client-cv-template.service';
import { BaseLayout } from '../../../interfaces/base-layout.interface';
import { ClientCvTemplate } from '../../../interfaces/client-cv-template.interface';

/**
 * Inline upload pane for the CV Studio. Replaces the old modal.
 *
 * Context aware:
 *   - No prefill: full form with base-layout dropdown.
 *   - prefilledLayoutId set: dropdown collapses to a "Based on X / Change" chip.
 *   - replaceTemplateId set: chip is fixed (no Change), title says "Replace files
 *     for <name>", file inputs default to optional, submit reads "Save". Matches
 *     the old modal replace mode (metadata-only persistence today).
 */
@Component({
  selector: 'bst-cv-studio-pane-upload',
  templateUrl: './studio-pane-upload.component.html',
  styleUrls: ['./studio-pane-upload.component.scss'],
  standalone: false,
})
export class StudioPaneUploadComponent implements OnChanges {

  @Input() public clientId!: number;
  @Input() public prefilledLayoutId?: string;
  @Input() public replaceTemplateId?: number;
  @Input() public replaceTemplateName?: string;

  @Output() public templateCreated = new EventEmitter<ClientCvTemplate>();
  @Output() public cancelled = new EventEmitter<void>();

  public baseLayouts: ReadonlyArray<BaseLayout> = [];

  public readonly maxBytes = 10 * 1024 * 1024;
  public readonly allowedMimes = ['application/pdf', 'image/png', 'image/jpeg'];

  public form: FormGroup;
  public firstPageFile: File | null = null;
  public continuationFile: File | null = null;
  public submitting = false;
  public fileError: string | null = null;
  public fieldErrors: { [field: string]: string } = {};
  public dragSlot: 'first' | 'continuation' | null = null;
  public showLayoutDropdown = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly service: ClientCvTemplateService,
    private readonly toastr: NbToastrService,
    private readonly translate: TranslateService,
  ) {
    this.form = this.fb.group({
      name:           [null, [Validators.required, Validators.maxLength(80)]],
      base_layout_id: [null, Validators.required],
      is_active:      [true],
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['prefilledLayoutId'] || changes['replaceTemplateId'] || changes['replaceTemplateName']) {
      this.showLayoutDropdown = !this.prefilledLayoutId;
      if (this.prefilledLayoutId) {
        this.form.patchValue({ base_layout_id: this.prefilledLayoutId });
      }
      if (this.replaceTemplateName) {
        this.form.patchValue({ name: this.replaceTemplateName });
      }
    }
    if (this.baseLayouts.length === 0) {
      this.service.listBaseLayouts().subscribe({
        next: rows => { this.baseLayouts = rows; },
        error: err => {
          // Without this toast the form silently disables (canSubmit
          // returns false because base_layout_id stays null) and the
          // user has no idea why the upload button isn't clickable.
          console.error('studio-pane-upload: listBaseLayouts failed', err);
          this.toastr.danger(
            this.translate.instant('cv_templates.errors.layouts_unavailable') || 'Could not load template layouts. Refresh to try again.',
            this.translate.instant('error') || 'Error',
          );
        },
      });
    }
  }

  public get isReplaceMode(): boolean {
    return this.replaceTemplateId != null;
  }

  public get canSubmit(): boolean {
    const name = (this.form.value.name || '').toString().trim();
    if (this.submitting || name.length === 0) return false;
    // Base layout is required on create. Without this check the button stays
    // clickable and submit() silently bails on form.invalid, leaving the user
    // wondering why nothing happened.
    if (!this.isReplaceMode && !this.form.value.base_layout_id) return false;
    return this.isReplaceMode || !!this.firstPageFile;
  }

  public baseLayoutName(id?: string | null): string {
    if (!id) return '';
    return this.baseLayouts.find(b => b.id === id)?.name ?? id;
  }

  public changeBase(): void {
    if (this.isReplaceMode) return;
    this.prefilledLayoutId = undefined;
    this.showLayoutDropdown = true;
    this.form.patchValue({ base_layout_id: null });
  }

  public downloadBaseSample(): void {
    const id = this.form.value.base_layout_id;
    if (!id) return;
    window.open(this.service.baseLayoutSamplePdfUrl(id), '_blank');
  }

  public onFirstPagePicked(event: Event): void {
    this.firstPageFile = this.pickFile(event);
  }

  public onContinuationPicked(event: Event): void {
    this.continuationFile = this.pickFile(event);
  }

  public onDragOver(event: DragEvent, slot: 'first' | 'continuation'): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragSlot = slot;
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragSlot = null;
  }

  public onDrop(event: DragEvent, slot: 'first' | 'continuation'): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragSlot = null;
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    const validated = this.validateFile(file);
    if (slot === 'first') this.firstPageFile = validated;
    else this.continuationFile = validated;
  }

  private pickFile(event: Event): File | null {
    const input = event.target as HTMLInputElement;
    return this.validateFile(input.files?.[0] ?? null);
  }

  private validateFile(file: File | null): File | null {
    this.fileError = null;
    if (!file) return null;
    if (!this.allowedMimes.includes(file.type)) {
      this.fileError = this.translate.instant('cv_templates.errors.unsupported_mime');
      return null;
    }
    if (file.size > this.maxBytes) {
      this.fileError = this.translate.instant('cv_templates.errors.file_too_large');
      return null;
    }
    return file;
  }

  public submit(): void {
    if (this.form.invalid || (!this.isReplaceMode && !this.firstPageFile)) {
      this.form.markAllAsTouched();
      if (!this.isReplaceMode && !this.firstPageFile) {
        this.fileError = this.translate.instant('cv_templates.errors.first_page_required');
      }
      return;
    }

    this.submitting = true;
    this.fieldErrors = {};
    const v = this.form.value;

    const onError = (err: HttpErrorResponse) => {
      this.submitting = false;
      if (err?.status === 422 && err.error?.errors) {
        const errors = err.error.errors as { [field: string]: string[] };
        Object.keys(errors).forEach(f => { this.fieldErrors[f] = errors[f][0]; });
      } else {
        this.toastr.danger('', this.translate.instant('cv_templates.errors.save_failed'));
      }
    };

    if (this.isReplaceMode) {
      this.service.update(this.clientId, this.replaceTemplateId!, {
        name:      v.name,
        is_active: v.is_active,
      }).subscribe({
        next: updated => {
          this.submitting = false;
          this.toastr.success('', this.translate.instant('cv_templates.saved_toast'));
          this.templateCreated.emit(updated);
        },
        error: onError,
      });
      return;
    }

    this.service.create(this.clientId, {
      name:              v.name,
      base_layout_id:    v.base_layout_id,
      is_active:         v.is_active,
      first_page_file:   this.firstPageFile!,
      continuation_file: this.continuationFile ?? undefined,
    }).subscribe({
      next: created => {
        this.submitting = false;
        this.toastr.success('', this.translate.instant('cv_templates.saved_toast'));
        this.templateCreated.emit(created);
      },
      error: onError,
    });
  }

  public cancel(): void {
    this.cancelled.emit();
  }
}
